import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  ArrowLeft, 
  Users, 
  TrendingUp, 
  ClipboardList, 
  BarChart3,
  FileText,
  UserCheck,
  Brain,
  CheckCircle,
  AlertCircle,
  Target,
  Calendar,
  BookOpen,
  Shield,
  Sparkles,
  ArrowRight,
  Loader2,
  X,
  Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fake sample data for mockups
const SAMPLE_DATA = {
  students: [
    { name: 'Avery Miller', grade: '3rd', tier: 2, area: 'Reading', status: 'Active' },
    { name: 'Jordan Lee', grade: '4th', tier: 2, area: 'Math', status: 'Active' },
    { name: 'Marcus Johnson', grade: '2nd', tier: 3, area: 'Reading', status: 'Active' },
    { name: 'Sofia Ramirez', grade: '5th', tier: 2, area: 'Behavior', status: 'Active' },
  ],
  staff: [
    { name: 'Ms. Parker', type: 'Reading Specialist', isProvider: true },
    { name: 'Mr. Brooks', type: 'Classroom Teacher', isProvider: false },
    { name: 'Dr. Lane', type: 'School Psychologist', isProvider: true },
    { name: 'Ms. Nguyen', type: 'Behavior Specialist', isProvider: true },
  ],
  dashboardStats: {
    activeStudents: 24,
    tier1: 12,
    tier2: 8,
    tier3: 4,
    needsReview: 3,
    upcomingReviews: 5,
  },
  progressData: [
    { date: 'Week 1', score: 45 },
    { date: 'Week 2', score: 48 },
    { date: 'Week 3', score: 52 },
    { date: 'Week 4', score: 55 },
    { date: 'Week 5', score: 58 },
    { date: 'Week 6', score: 62 },
  ],
};

// Mock Screenshot Card Component - Premium showcase styling
const MockScreenshot = ({ title, children, caption }) => (
  <div className="group bg-white rounded-2xl border border-[#E0E0E0] overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 motion-reduce:hover:translate-y-0 motion-reduce:transition-none">
    <div className="bg-gradient-to-b from-[#FAFAFA] to-[#F5F5F5] px-4 py-2.5 border-b border-[#E5E5E5] flex items-center gap-2">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-[#FF6058] shadow-sm"></div>
        <div className="w-3 h-3 rounded-full bg-[#FFBD2E] shadow-sm"></div>
        <div className="w-3 h-3 rounded-full bg-[#28CA42] shadow-sm"></div>
      </div>
      <span className="text-xs font-medium text-[#595959] ml-2">{title}</span>
      <span className="ml-auto text-xs text-[#A3A3A3] italic">Sample screenshot</span>
    </div>
    <div className="p-5 bg-white">
      {children}
    </div>
    {caption && (
      <div className="px-5 py-3.5 bg-gradient-to-b from-[#FAFAFA] to-[#F5F5F5] border-t border-[#E5E5E5]">
        <p className="text-sm text-[#595959] italic leading-relaxed">{caption}</p>
      </div>
    )}
  </div>
);

// Dashboard Mockup
const DashboardMockup = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-semibold text-[#1C1C1C]">Dashboard</h3>
      <span className="text-xs bg-[#2C4C3B]/10 text-[#2C4C3B] px-2 py-1 rounded-full">Sample MTSS Academy</span>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-[#F0F7F4] rounded-xl p-3 text-center">
        <div className="text-2xl font-bold text-[#2C4C3B]">{SAMPLE_DATA.dashboardStats.activeStudents}</div>
        <div className="text-xs text-[#595959]">Active Students</div>
      </div>
      <div className="bg-blue-50 rounded-xl p-3 text-center">
        <div className="text-2xl font-bold text-blue-600">{SAMPLE_DATA.dashboardStats.tier2}</div>
        <div className="text-xs text-[#595959]">Tier 2 Students</div>
      </div>
      <div className="bg-orange-50 rounded-xl p-3 text-center">
        <div className="text-2xl font-bold text-orange-600">{SAMPLE_DATA.dashboardStats.tier3}</div>
        <div className="text-xs text-[#595959]">Tier 3 Students</div>
      </div>
      <div className="bg-red-50 rounded-xl p-3 text-center">
        <div className="text-2xl font-bold text-red-600">{SAMPLE_DATA.dashboardStats.needsReview}</div>
        <div className="text-xs text-[#595959]">Needs Review</div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
      <div className="border border-[#E5E5E5] rounded-xl p-3">
        <h4 className="text-sm font-medium text-[#1C1C1C] mb-2">Students Needing Attention</h4>
        <div className="space-y-2">
          {SAMPLE_DATA.students.slice(0, 2).map((s, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-[#595959]">{s.name}</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Review Due</span>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-[#E5E5E5] rounded-xl p-3">
        <h4 className="text-sm font-medium text-[#1C1C1C] mb-2">Upcoming Reviews</h4>
        <div className="space-y-2">
          {SAMPLE_DATA.students.slice(2, 4).map((s, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-[#595959]">{s.name}</span>
              <span className="text-xs text-[#A3A3A3]">Jan {15 + i}, 2026</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Student Profile Mockup
const StudentProfileMockup = () => (
  <div className="space-y-4">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#2C4C3B] flex items-center justify-center text-white font-bold text-lg">
        A
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-[#1C1C1C]">Avery Miller</h3>
        <div className="flex flex-wrap gap-2 mt-1">
          <span className="text-xs bg-[#E5E5E5] text-[#595959] px-2 py-0.5 rounded">Grade 3</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Tier 2</span>
          <span className="text-xs bg-[#F0F7F4] text-[#2C4C3B] px-2 py-0.5 rounded">Reading</span>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
        </div>
        <p className="text-xs text-[#A3A3A3] mt-1">Teacher: Mr. Brooks • Sample MTSS Academy</p>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3 text-center border-t border-[#E5E5E5] pt-4">
      <div>
        <div className="text-lg font-semibold text-[#1C1C1C]">45</div>
        <div className="text-xs text-[#595959]">Baseline</div>
      </div>
      <div>
        <div className="text-lg font-semibold text-[#2C4C3B]">62</div>
        <div className="text-xs text-[#595959]">Latest Score</div>
      </div>
      <div>
        <div className="text-lg font-semibold text-green-600">+17</div>
        <div className="text-xs text-[#595959]">Growth</div>
      </div>
    </div>
    <div className="flex gap-2 text-xs">
      <button className="flex-1 py-2 bg-[#F0F7F4] text-[#2C4C3B] rounded-lg font-medium">Overview</button>
      <button className="flex-1 py-2 text-[#595959] rounded-lg">Interventions</button>
      <button className="flex-1 py-2 text-[#595959] rounded-lg">Data</button>
      <button className="flex-1 py-2 text-[#595959] rounded-lg">Context</button>
    </div>
  </div>
);

// Intervention Mockup
const InterventionMockup = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="font-medium text-[#1C1C1C]">Active Interventions</h4>
      <span className="text-xs bg-[#2C4C3B]/10 text-[#2C4C3B] px-2 py-1 rounded-full">2 Active</span>
    </div>
    <div className="border border-[#E5E5E5] rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h5 className="font-medium text-[#1C1C1C]">Phonics-Based Reading Intervention</h5>
          <p className="text-xs text-[#595959] mt-0.5">Target: Decoding multisyllabic words</p>
        </div>
        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-[#A3A3A3] text-xs">Provider</span>
          <p className="text-[#595959]">Ms. Parker</p>
        </div>
        <div>
          <span className="text-[#A3A3A3] text-xs">Frequency</span>
          <p className="text-[#595959]">3x per week, 30 min</p>
        </div>
        <div>
          <span className="text-[#A3A3A3] text-xs">Start Date</span>
          <p className="text-[#595959]">Nov 1, 2025</p>
        </div>
        <div>
          <span className="text-[#A3A3A3] text-xs">Review Date</span>
          <p className="text-[#595959]">Jan 15, 2026</p>
        </div>
      </div>
    </div>
  </div>
);

// Progress Mockup with SVG Line Chart
const ProgressMockup = () => {
  const data = SAMPLE_DATA.progressData;
  const maxScore = 70;
  const minScore = 40;
  const chartWidth = 280;
  const chartHeight = 100;
  const padding = { top: 10, right: 10, bottom: 25, left: 30 };
  const graphWidth = chartWidth - padding.left - padding.right;
  const graphHeight = chartHeight - padding.top - padding.bottom;
  
  // Calculate points for the line
  const points = data.map((d, i) => ({
    x: padding.left + (i / (data.length - 1)) * graphWidth,
    y: padding.top + graphHeight - ((d.score - minScore) / (maxScore - minScore)) * graphHeight,
    score: d.score,
    week: d.date
  }));
  
  // Create path for the line
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  // Create area fill path
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + graphHeight} L ${points[0].x} ${padding.top + graphHeight} Z`;
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-[#1C1C1C]">Progress Monitoring</h4>
        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          Improving
        </span>
      </div>
      
      {/* SVG Line Chart */}
      <div className="bg-[#F8F8F8] rounded-lg p-3">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-auto">
          {/* Horizontal grid lines */}
          {[40, 50, 60, 70].map((val, i) => {
            const y = padding.top + graphHeight - ((val - minScore) / (maxScore - minScore)) * graphHeight;
            return (
              <g key={i}>
                <line 
                  x1={padding.left} 
                  y1={y} 
                  x2={chartWidth - padding.right} 
                  y2={y} 
                  stroke="#E5E5E5" 
                  strokeWidth="1"
                  strokeDasharray={val === 40 ? "0" : "4,4"}
                />
                <text 
                  x={padding.left - 5} 
                  y={y + 3} 
                  textAnchor="end" 
                  className="text-[8px] fill-[#A3A3A3]"
                >
                  {val}
                </text>
              </g>
            );
          })}
          
          {/* Area fill under line */}
          <path 
            d={areaPath} 
            fill="url(#progressGradient)" 
            opacity="0.3"
          />
          
          {/* Gradient definition */}
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2C4C3B" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#2C4C3B" stopOpacity="0" />
            </linearGradient>
          </defs>
          
          {/* Main line */}
          <path 
            d={linePath} 
            fill="none" 
            stroke="#2C4C3B" 
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle 
                cx={p.x} 
                cy={p.y} 
                r="4" 
                fill="white" 
                stroke="#2C4C3B" 
                strokeWidth="2"
              />
              {/* Score label above point */}
              <text 
                x={p.x} 
                y={p.y - 8} 
                textAnchor="middle" 
                className="text-[8px] fill-[#2C4C3B] font-medium"
              >
                {p.score}
              </text>
            </g>
          ))}
          
          {/* X-axis labels */}
          {points.map((p, i) => (
            <text 
              key={i}
              x={p.x} 
              y={chartHeight - 5} 
              textAnchor="middle" 
              className="text-[7px] fill-[#A3A3A3]"
            >
              Wk {i + 1}
            </text>
          ))}
        </svg>
      </div>
      
      <div className="border-t border-[#E5E5E5] pt-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#595959]">
            <span className="font-medium text-[#1C1C1C]">Latest:</span> 62 (Jan 8, 2026)
          </span>
          <span className="text-xs text-[#A3A3A3]">Entered by Ms. Parker</span>
        </div>
      </div>
    </div>
  );
};

// Context Profile Mockup
const ContextProfileMockup = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="font-medium text-[#1C1C1C]">Student Context Profile</h4>
      <div className="flex items-center gap-2">
        <div className="w-20 h-2 bg-[#E5E5E5] rounded-full overflow-hidden">
          <div className="w-3/4 h-full bg-[#2C4C3B]"></div>
        </div>
        <span className="text-xs text-[#595959]">75% Complete</span>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="border border-[#E5E5E5] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-[#1C1C1C]">Student Strengths</span>
        </div>
        <p className="text-xs text-[#595959]">Strong verbal comprehension, motivated learner, positive peer relationships</p>
      </div>
      <div className="border border-[#E5E5E5] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-[#1C1C1C]">Current Concerns</span>
        </div>
        <p className="text-xs text-[#595959]">Difficulty with decoding, below grade level in fluency</p>
      </div>
      <div className="border border-[#E5E5E5] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-[#1C1C1C]">Parent/Guardian Input</span>
        </div>
        <p className="text-xs text-[#595959]">Family supportive of intervention, practices reading at home</p>
      </div>
      <div className="border border-dashed border-[#E5E5E5] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-4 h-4 text-[#A3A3A3]" />
          <span className="text-sm font-medium text-[#A3A3A3]">Attendance Data</span>
        </div>
        <p className="text-xs text-[#A3A3A3]">Not yet documented</p>
      </div>
    </div>
  </div>
);

// Staff Directory Mockup
const StaffDirectoryMockup = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="font-medium text-[#1C1C1C]">Staff Directory</h4>
      <span className="text-xs text-[#595959]">4 staff members</span>
    </div>
    <div className="space-y-2">
      {SAMPLE_DATA.staff.map((s, i) => (
        <div key={i} className="flex items-center justify-between p-3 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#F0F7F4] flex items-center justify-center text-[#2C4C3B] font-medium text-sm">
              {s.name.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-medium text-[#1C1C1C]">{s.name}</p>
              <p className="text-xs text-[#595959]">{s.type}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {s.isProvider && (
              <span className="text-xs bg-[#F0F7F4] text-[#2C4C3B] px-2 py-0.5 rounded">Provider</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Reports Mockup
const ReportsMockup = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="font-medium text-[#1C1C1C]">AI Progress Summary</h4>
      <span className="flex items-center gap-1 text-xs text-[#2C4C3B]">
        <Sparkles className="w-3 h-3" />
        Generated
      </span>
    </div>
    <div className="bg-[#F8F8F8] rounded-xl p-4 border border-[#E5E5E5]">
      <p className="text-sm text-[#595959] leading-relaxed">
        <strong>Avery Miller</strong> has shown consistent improvement in reading intervention over the past 6 weeks. 
        Baseline score of 45 has increased to 62, representing a <strong>38% improvement</strong>. 
        The student responds well to phonics-based instruction with Ms. Parker. 
        Current trajectory suggests the student is on track to meet the goal of 70 by the February review date. 
        Continued intervention at current intensity is recommended.
      </p>
    </div>
    <p className="text-xs text-[#A3A3A3] italic">
      This summary is AI-generated and must be reviewed by qualified school personnel before use in official documents.
    </p>
  </div>
);

// Feature List Item Component - Polished checkmark items
const FeatureListItem = ({ children }) => (
  <li className="flex items-center gap-3 text-sm text-[#595959]">
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#F0F7F4] flex items-center justify-center">
      <CheckCircle className="w-3.5 h-3.5 text-[#2C4C3B]" />
    </span>
    {children}
  </li>
);

// Section Title with Icon - Enhanced styling
const SectionTitle = ({ icon: Icon, children }) => (
  <h3 className="text-xl font-semibold text-[#1C1C1C] mb-3 flex items-center gap-3">
    <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0F7F4] to-[#E5F0EA] flex items-center justify-center shadow-sm">
      <Icon className="w-5 h-5 text-[#2C4C3B]" />
    </span>
    {children}
  </h3>
);

// Workflow Step Component - Enhanced with card-like treatment
const WorkflowStep = ({ number, title, description, icon: Icon }) => (
  <div className="group flex gap-5 items-start p-5 rounded-xl bg-white border border-[#E5E5E5] shadow-sm hover:shadow-md hover:border-[#2C4C3B]/20 transition-all duration-300 ease-out hover:-translate-y-0.5 motion-reduce:hover:translate-y-0 motion-reduce:transition-none">
    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#2C4C3B] to-[#1E3629] text-white flex items-center justify-center font-bold text-lg shadow-md">
      {number}
    </div>
    <div className="flex-1 pt-1">
      <h4 className="font-semibold text-[#1C1C1C] flex items-center gap-2 mb-1.5">
        {Icon && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#F0F7F4]">
            <Icon className="w-3.5 h-3.5 text-[#2C4C3B]" />
          </span>
        )}
        {title}
      </h4>
      <p className="text-sm text-[#595959] leading-relaxed">{description}</p>
    </div>
  </div>
);

// Request Access Modal Component
const RequestAccessModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_name: '',
    contact_role: '',
    contact_email: '',
    contact_phone: '',
    approximate_size: '',
    use_case: '',
    website: '' // Honeypot field - should remain empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/public/request-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit request');
      }

      if (data.success) {
        setIsSuccess(true);
        toast.success('Request submitted successfully!');
      } else {
        // Email send failed
        setErrorMessage(data.message || 'Something went wrong. Please try again or email simplyslps@gmail.com directly.');
        toast.error('Failed to submit request');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Something went wrong while sending your request. Please email simplyslps@gmail.com directly.');
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      organization_name: '',
      contact_name: '',
      contact_role: '',
      contact_email: '',
      contact_phone: '',
      approximate_size: '',
      use_case: '',
      website: ''
    });
    setIsSuccess(false);
    setErrorMessage('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-white border border-[#E5E5E5] shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-[#1C1C1C] flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#2C4C3B]" />
            Request Access to Frame Interventions
          </DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-2">Request Submitted!</h3>
            <p className="text-[#595959] mb-6">
              Thank you for your interest in Frame Interventions. We'll be in touch soon to discuss pilot or demo access.
            </p>
            <Button onClick={handleClose} className="bg-[#2C4C3B] hover:bg-[#1E3629]">
              Close
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#595959] mb-4">
              Tell us a little about your school or organization, and we'll follow up about pilot or demo access.
            </p>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - hidden from users, visible to bots */}
              <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  name="website"
                  type="text"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_name" className="text-sm font-medium text-[#1C1C1C]">
                  School / Organization Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organization_name"
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  placeholder="e.g., Lincoln Elementary School"
                  required
                  maxLength={120}
                  className="rounded-lg bg-white border-[#E5E5E5]"
                  data-testid="access-form-org"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name" className="text-sm font-medium text-[#1C1C1C]">
                    Your Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder="Full name"
                    required
                    maxLength={120}
                    className="rounded-lg bg-white border-[#E5E5E5]"
                    data-testid="access-form-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_role" className="text-sm font-medium text-[#1C1C1C]">
                    Your Role <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact_role"
                    name="contact_role"
                    value={formData.contact_role}
                    onChange={handleChange}
                    placeholder="e.g., MTSS Coordinator"
                    required
                    maxLength={120}
                    className="rounded-lg bg-white border-[#E5E5E5]"
                    data-testid="access-form-role"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="text-sm font-medium text-[#1C1C1C]">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="you@school.edu"
                    required
                    maxLength={180}
                    className="rounded-lg bg-white border-[#E5E5E5]"
                    data-testid="access-form-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="text-sm font-medium text-[#1C1C1C]">
                    Phone <span className="text-[#A3A3A3]">(optional)</span>
                  </Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    maxLength={50}
                    className="rounded-lg bg-white border-[#E5E5E5]"
                    data-testid="access-form-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="approximate_size" className="text-sm font-medium text-[#1C1C1C]">
                  Approximate Number of Students/Staff <span className="text-[#A3A3A3]">(optional)</span>
                </Label>
                <Input
                  id="approximate_size"
                  name="approximate_size"
                  value={formData.approximate_size}
                  onChange={handleChange}
                  placeholder="e.g., 500 students, 50 staff"
                  maxLength={80}
                  className="rounded-lg bg-white border-[#E5E5E5]"
                  data-testid="access-form-size"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="use_case" className="text-sm font-medium text-[#1C1C1C]">
                  What are you hoping to use Frame Interventions for? <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="use_case"
                  name="use_case"
                  value={formData.use_case}
                  onChange={handleChange}
                  placeholder="Tell us about your MTSS goals, current challenges, or what you're looking for..."
                  rows={3}
                  required
                  maxLength={1500}
                  className="rounded-lg resize-none bg-white border-[#E5E5E5]"
                  data-testid="access-form-usecase"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 rounded-lg border-[#E5E5E5]"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#2C4C3B] hover:bg-[#1E3629] rounded-lg"
                  disabled={isSubmitting}
                  data-testid="access-form-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Explore Page Component
export default function ExplorePage() {
  const [showRequestModal, setShowRequestModal] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="explore-page">
      {/* Request Access Modal */}
      <RequestAccessModal 
        isOpen={showRequestModal} 
        onClose={() => setShowRequestModal(false)} 
      />

      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-[#1C1C1C]">Frame Interventions</h1>
              <p className="text-xs text-[#595959]">MTSS Progress Tracker</p>
            </div>
          </div>
          <Link to="/login">
            <Button 
              variant="outline" 
              className="border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#F0F7F4]"
              data-testid="return-to-login-header"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2C4C3B] to-[#1E3629] text-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-6">
            <span className="text-sm">Product Tour</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Intervention data that leads to<br />better decisions
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto mb-8">
            Frame Interventions helps school teams track supports, monitor progress, 
            collect student context, and make clearer MTSS decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button 
                size="lg" 
                className="bg-white text-[#2C4C3B] hover:bg-white/90 px-8"
                data-testid="hero-return-to-login"
              >
                Return to Login
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10 px-8"
              data-testid="hero-request-access"
              onClick={() => setShowRequestModal(true)}
            >
              Request Access
            </Button>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1C1C] mb-4">
              The challenge schools face
            </h2>
            <p className="text-[#595959] max-w-2xl mx-auto">
              Many schools struggle with intervention tracking because the right information 
              isn't in the right place at the right time.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ClipboardList, title: 'Scattered Data', desc: 'Intervention records live in spreadsheets, paper files, and disconnected systems' },
              { icon: TrendingUp, title: 'Unclear Progress', desc: 'Teams struggle to see whether students are actually responding to supports' },
              { icon: FileText, title: 'Limited Documentation', desc: 'Referral decisions happen with incomplete or inconsistent data' },
              { icon: Users, title: 'Fragmented Teams', desc: 'Teachers, specialists, and administrators need a clearer shared process' },
            ].map((item, i) => (
              <div 
                key={i} 
                className="group relative bg-white rounded-2xl p-6 text-center border border-[#E0E0E0] shadow-lg hover:shadow-xl transition-all duration-300 ease-out hover:-translate-y-1.5 motion-reduce:hover:translate-y-0 motion-reduce:transition-none overflow-hidden"
              >
                {/* Subtle top accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2C4C3B] to-[#3D6B52]"></div>
                
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E8F5EE] to-[#D4EBE0] flex items-center justify-center mx-auto mb-5 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-300">
                  <item.icon className="w-8 h-8 text-[#2C4C3B]" />
                </div>
                <h3 className="font-bold text-[#1C1C1C] mb-2.5 text-lg">{item.title}</h3>
                <p className="text-sm text-[#595959] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The FrameEd Approach */}
      <section className="py-16 bg-[#F8F8F8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1C1C] mb-4">
              The FrameEd approach
            </h2>
            <p className="text-[#595959] max-w-2xl mx-auto">
              A structured workflow that connects identification, intervention, data collection, and decision-making.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-6">
            <WorkflowStep 
              number={1} 
              icon={Target}
              title="Identify student need" 
              description="Document which students need support and in what area (reading, math, behavior, etc.)"
            />
            <WorkflowStep 
              number={2} 
              icon={BookOpen}
              title="Assign structured support" 
              description="Connect students to specific interventions, providers, and schedules"
            />
            <WorkflowStep 
              number={3} 
              icon={TrendingUp}
              title="Track progress over time" 
              description="Record regular progress data points tied to goals and baselines"
            />
            <WorkflowStep 
              number={4} 
              icon={ClipboardList}
              title="Collect context information" 
              description="Gather teacher input, parent input, attendance data, and student strengths"
            />
            <WorkflowStep 
              number={5} 
              icon={Calendar}
              title="Review response to intervention" 
              description="Use the data to evaluate whether supports are working"
            />
            <WorkflowStep 
              number={6} 
              icon={Users}
              title="Support team decisions" 
              description="Provide clear information for next-step conversations"
            />
          </div>
        </div>
      </section>

      {/* Product Tour Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold tracking-wide text-[#2C4C3B] bg-gradient-to-r from-[#F0F7F4] to-[#E5F0EA] px-4 py-1.5 rounded-full mb-4 shadow-sm border border-[#2C4C3B]/10">
              PRODUCT TOUR
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1C1C] mb-4">
              See how it works
            </h2>
            <p className="text-[#595959] max-w-2xl mx-auto">
              Explore the key features of Frame Interventions with sample data.
            </p>
          </div>

          <div className="space-y-20">
            {/* Dashboard */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="lg:pr-4">
                <SectionTitle icon={BarChart3}>Dashboard Overview</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  See which students need attention and where support is happening across the school. 
                  The dashboard provides at-a-glance metrics for active students, tier distribution, 
                  upcoming reviews, and students needing immediate attention.
                </p>
                <ul className="space-y-3">
                  {['Active student counts by tier', 'Students needing review', 'Upcoming review dates', 'Recent progress entries'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
              <MockScreenshot title="Dashboard" caption="See which students need attention and where support is happening across the school.">
                <DashboardMockup />
              </MockScreenshot>
            </div>

            {/* Student Profile */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1">
                <MockScreenshot title="Student Profile" caption="Each student has a clear profile that connects supports, data, notes, and review information.">
                  <StudentProfileMockup />
                </MockScreenshot>
              </div>
              <div className="order-1 lg:order-2 lg:pl-4">
                <SectionTitle icon={Users}>Student Profiles</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  Each student has a comprehensive profile that brings together their support information, 
                  progress data, intervention history, and context in one place.
                </p>
                <ul className="space-y-3">
                  {['Support area and tier assignment', 'Teacher and provider connections', 'Progress trend visualization', 'Intervention history'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
            </div>

            {/* Intervention Tracking */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="lg:pr-4">
                <SectionTitle icon={ClipboardList}>Intervention Tracking</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  Document interventions with structured information including provider, frequency, 
                  duration, target skills, and review dates. Keep everything organized and connected.
                </p>
                <ul className="space-y-3">
                  {['Structured provider assignment', 'Frequency and duration tracking', 'Target skill documentation', 'Review date tracking'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
              <MockScreenshot title="Interventions" caption="Interventions are connected to structured providers, support areas, and review timelines.">
                <InterventionMockup />
              </MockScreenshot>
            </div>

            {/* Progress Monitoring */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1">
                <MockScreenshot title="Progress Data" caption="Progress data is tied to the logged-in user so teams know who entered each data point.">
                  <ProgressMockup />
                </MockScreenshot>
              </div>
              <div className="order-1 lg:order-2 lg:pl-4">
                <SectionTitle icon={TrendingUp}>Progress Monitoring</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  Track progress data over time with visual charts and clear trends. Every data point 
                  is attributed to the user who entered it for accountability and clarity.
                </p>
                <ul className="space-y-3">
                  {['Visual progress charts', 'Trend analysis', 'Data entry attribution', 'Goal tracking'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
            </div>

            {/* Context Profile */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="lg:pr-4">
                <SectionTitle icon={FileText}>Student Context Profile</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  Collect the broader student story over time, not just test scores. Document strengths, 
                  concerns, parent input, teacher observations, and other contextual information.
                </p>
                <ul className="space-y-3">
                  {['Student strengths and interests', 'Current concerns and needs', 'Parent/guardian input', 'Attendance and engagement data', 'Completeness tracking'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
              <MockScreenshot title="Context Profile" caption="Context Profile helps teams collect the broader student story over time, not just scores.">
                <ContextProfileMockup />
              </MockScreenshot>
            </div>

            {/* Staff Directory */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1">
                <MockScreenshot title="Staff Directory" caption="Frame Interventions separates app access from staff identity so schools can manage users, teachers, and providers clearly.">
                  <StaffDirectoryMockup />
                </MockScreenshot>
              </div>
              <div className="order-1 lg:order-2 lg:pl-4">
                <SectionTitle icon={UserCheck}>Staff & Provider Structure</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  Manage your staff directory separately from app users. Track who can provide interventions, 
                  who has login access, and connect staff to student assignments clearly.
                </p>
                <ul className="space-y-3">
                  {['Staff directory management', 'Provider designation', 'User/staff linkage', 'Role-based access'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
            </div>

            {/* Reports & AI */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="lg:pr-4">
                <SectionTitle icon={Brain}>Reports & AI Summary</SectionTitle>
                <p className="text-[#595959] mb-5 leading-relaxed">
                  Generate AI-powered progress summaries to help prepare for meetings and communicate 
                  student progress clearly. All AI content must be reviewed by qualified staff.
                </p>
                <ul className="space-y-3">
                  {['AI-generated progress summaries', 'Meeting preparation support', 'Export capabilities', 'Human review required'].map((item, i) => (
                    <FeatureListItem key={i}>{item}</FeatureListItem>
                  ))}
                </ul>
              </div>
              <MockScreenshot title="AI Summary" caption="Reports and summaries help teams prepare for meetings and communicate progress clearly.">
                <ReportsMockup />
              </MockScreenshot>
            </div>
          </div>
        </div>
      </section>

      {/* Future Vision */}
      <section className="py-16 bg-gradient-to-b from-[#F8F8F8] to-[#F3F4F1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-semibold tracking-wide text-[#2C4C3B] bg-gradient-to-r from-[#F0F7F4] to-[#E5F0EA] px-4 py-1.5 rounded-full mb-4 shadow-sm border border-[#2C4C3B]/10">
              FUTURE VISION
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1C1C] mb-4">
              The FrameEd ecosystem
            </h2>
            <p className="text-[#595959] max-w-2xl mx-auto">
              Frame Interventions is part of a larger vision to support the complete special education workflow.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E0E0E0] p-6 sm:p-10 max-w-4xl mx-auto shadow-md">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
              <div className="text-center px-5 py-4 bg-gradient-to-br from-[#F0F7F4] to-[#E5F0EA] rounded-xl border-2 border-[#2C4C3B] shadow-sm">
                <p className="font-semibold text-[#2C4C3B]">Frame Interventions</p>
                <p className="text-xs text-[#595959]">MTSS Tracking</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#C5C5C5] rotate-90 sm:rotate-0 flex-shrink-0" />
              <div className="text-center px-5 py-4 bg-gradient-to-b from-white to-[#FAFAFA] rounded-xl border border-dashed border-[#D0D0D0]">
                <p className="font-semibold text-[#6B6B6B]">Review of Existing Data</p>
                <p className="text-xs text-[#A3A3A3]">Coming Soon</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#C5C5C5] rotate-90 sm:rotate-0 flex-shrink-0" />
              <div className="text-center px-5 py-4 bg-gradient-to-b from-white to-[#FAFAFA] rounded-xl border border-dashed border-[#D0D0D0]">
                <p className="font-semibold text-[#6B6B6B]">Frame MET</p>
                <p className="text-xs text-[#A3A3A3]">Future</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#C5C5C5] rotate-90 sm:rotate-0 flex-shrink-0" />
              <div className="text-center px-5 py-4 bg-gradient-to-b from-white to-[#FAFAFA] rounded-xl border border-dashed border-[#D0D0D0]">
                <p className="font-semibold text-[#6B6B6B]">Frame IEP</p>
                <p className="text-xs text-[#A3A3A3]">Future</p>
              </div>
            </div>
            <p className="text-sm text-[#595959] text-center mt-8 max-w-2xl mx-auto leading-relaxed">
              FrameEd's long-term vision is to generate required special education documentation 
              from meaningful data collected over time, reducing paperwork and improving outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Responsible AI Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-18 h-18 rounded-2xl bg-gradient-to-br from-[#F0F7F4] to-[#E5F0EA] flex items-center justify-center mx-auto mb-6 shadow-sm p-4">
            <Shield className="w-9 h-9 text-[#2C4C3B]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1C1C1C] mb-4">
            Built to support teams, not replace them
          </h2>
          <p className="text-[#595959] leading-relaxed">
            Frame Interventions organizes data, highlights patterns, and supports team decision-making. 
            It does not determine special education eligibility, diagnose students, or replace professional judgment. 
            All AI-generated content must be reviewed by qualified school personnel before use in official records or decisions.
          </p>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-16 bg-gradient-to-br from-[#2C4C3B] to-[#1E3629] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Ready to see Frame Interventions with your team?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Contact us to learn how Frame Interventions can support your school's MTSS process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button 
                size="lg" 
                className="bg-white text-[#2C4C3B] hover:bg-white/90 px-8"
                data-testid="footer-return-to-login"
              >
                Return to Login
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10 px-8"
              data-testid="footer-request-access"
              onClick={() => setShowRequestModal(true)}
            >
              Request Access
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1C1C] text-white/60 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#2C4C3B] flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-white">FrameEd</span>
          </div>
          <p className="text-sm">
            Frame Interventions is part of the FrameEd ecosystem.
          </p>
          <p className="text-xs mt-2">
            All sample data shown on this page is fictional and for demonstration purposes only.
          </p>
        </div>
      </footer>
    </div>
  );
}
